import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, FolderIcon, FolderOpenIcon } from '@phosphor-icons/react';
import { ListItem } from '@/components/List';
import { Modal, ModalVariant } from '@/components/Modal';
import { SearchInput } from '@/components/SearchInput';
import { SEARCH_DEBOUNCE_MS } from '@/constants/debounce';
import { FolderOwnership, SourceFolder } from '@/types/source-folder';
import { FullSource } from '@/types/source';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    useDebounce,
    useInfiniteScroll,
    useAsyncAction,
    useSourceFoldersInfinite,
} from '@/hooks';
import { cn } from '@/lib/utils';

const OWNED_FOLDERS: FolderOwnership = 'owned';

interface ManageFoldersModalProps {
    isOpen: boolean;
    sources: FullSource[];
    describedCount?: number;
    onClose: () => void;
    onSubmit: (changes: FolderMembershipIntent) => Promise<void>;
}

export interface FolderMembershipIntent {
    add: string[];
    remove: string[];
}

type Membership = Record<string, Set<string>>;

const DESCRIBED_MARKER = '__described__';
type FolderMembershipState = 'none' | 'partial' | 'all';

function buildInitialMembership(sources: FullSource[]): Membership {
    return sources.reduce<Membership>((acc, source) => {
        const folderIds = source.folderIds ?? [];
        return folderIds.reduce<Membership>((folderAcc, folderId) => {
            const existing = folderAcc[folderId];
            const sourceIds = existing
                ? [...Array.from(existing), source.id]
                : [source.id];
            return { ...folderAcc, [folderId]: new Set(sourceIds) };
        }, acc);
    }, {});
}

function getFolderMembershipState(
    folderId: string,
    membership: Membership,
    totalSources: number,
    isDescribed: boolean,
): FolderMembershipState {
    const count = membership[folderId]?.size ?? 0;
    if (count === 0) return 'none';
    if (isDescribed || count === totalSources) return 'all';
    return 'partial';
}

function moveSelectedFoldersFirst(
    folders: SourceFolder[],
    selectedFolderIds: Set<string>,
): SourceFolder[] {
    return [
        ...folders.filter((folder) => selectedFolderIds.has(folder.id)),
        ...folders.filter((folder) => !selectedFolderIds.has(folder.id)),
    ];
}

function computeDiff(
    initial: Membership,
    target: Membership,
    totalSources: number,
    isDescribed: boolean,
): FolderMembershipIntent {
    const allFolderIds = new Set<string>([
        ...Object.keys(initial),
        ...Object.keys(target),
    ]);
    const add: string[] = [];
    const remove: string[] = [];
    allFolderIds.forEach((folderId) => {
        const was = getFolderMembershipState(
            folderId,
            initial,
            totalSources,
            isDescribed,
        );
        const now = getFolderMembershipState(
            folderId,
            target,
            totalSources,
            isDescribed,
        );
        if (now === was) return;
        if (now === 'all') add.push(folderId);
        if (now === 'none') remove.push(folderId);
    });
    return { add, remove };
}

export function ManageFoldersModal({
    isOpen,
    sources,
    describedCount,
    onClose,
    onSubmit,
}: ManageFoldersModalProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const listRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const isDescribed = describedCount !== undefined;
    const sourceIds = useMemo(() => sources.map((s) => s.id), [sources]);
    const totalSources = isDescribed ? describedCount : sources.length;

    const initialMembership = useMemo(
        () => (isDescribed ? {} : buildInitialMembership(sources)),
        [isDescribed, sources],
    );

    const [targetMembership, setTargetMembership] =
        useState<Membership>(initialMembership);
    const [searchQuery, setSearchQuery] = useState('');
    const folderNameSearch = useDebounce(
        searchQuery.trim(),
        SEARCH_DEBOUNCE_MS,
    );

    useEffect(() => {
        if (isOpen) {
            setTargetMembership(initialMembership);
            setSearchQuery('');
        }
    }, [isOpen, initialMembership]);

    const toggleFolder = (folderId: string) => {
        setTargetMembership((previous) => {
            const state = getFolderMembershipState(
                folderId,
                previous,
                totalSources,
                isDescribed,
            );
            return {
                ...previous,
                [folderId]:
                    state === 'all'
                        ? new Set<string>()
                        : new Set(isDescribed ? [DESCRIBED_MARKER] : sourceIds),
            };
        });
    };

    const { data, size, isValidating, hasMore, loadMore } =
        useSourceFoldersInfinite(
            false,
            { revalidateOnFocus: false },
            {
                ownership: OWNED_FOLDERS,
                search: folderNameSearch || undefined,
            },
        );

    const folders = useMemo(
        () => (data ? data.flatMap((page) => page.items) : []),
        [data],
    );

    const initiallySelectedFolderIds = useMemo(
        () => new Set(Object.keys(initialMembership)),
        [initialMembership],
    );

    const orderedFolders = useMemo(
        () => moveSelectedFoldersFirst(folders, initiallySelectedFolderIds),
        [folders, initiallySelectedFolderIds],
    );

    const isLoadingMore = isValidating && size > 1;

    useInfiniteScroll(listRef.current, {
        onLoadMore: loadMore,
        isLoading: isLoadingMore,
        hasMore,
    });

    const diff = useMemo(
        () =>
            computeDiff(
                initialMembership,
                targetMembership,
                totalSources,
                isDescribed,
            ),
        [initialMembership, targetMembership, totalSources, isDescribed],
    );
    const hasChanges = diff.add.length > 0 || diff.remove.length > 0;

    const { isSubmitting, run } = useAsyncAction(async () => {
        await onSubmit(diff);
        onClose();
    });

    const handleSubmit = () => {
        if (hasChanges && !isSubmitting) run();
    };

    const handleOpenAutoFocus = (event: Event) => {
        event.preventDefault();
        searchInputRef.current?.focus();
    };

    const isSingle = totalSources === 1;
    const title = isSingle
        ? t('folders.manageFoldersTitle')
        : t('folders.manageFoldersTitleMulti', { count: totalSources });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            onCancel={onClose}
            onSubmit={handleSubmit}
            submitText={t('folders.manageFoldersSubmit')}
            submitDisabled={!hasChanges}
            submitInProgress={isSubmitting}
            maxWidth="max-w-[521px]"
            maxHeight="max-h-[500px]"
            variant={ModalVariant.DEFAULT}
            fullHeight
            contentScrollUnderFooter
            scrollContainerRef={listRef}
            onOpenAutoFocus={handleOpenAutoFocus}
        >
            <div className="sticky top-0 z-10 bg-grey-700 px-4 pt-2">
                <SearchInput
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t('folders.searchFoldersPlaceholder')}
                    data-testid="manage-folders-modal-search"
                />
            </div>
            <div className="flex flex-col gap-0 px-4 py-4">
                {orderedFolders.length === 0 && !isValidating ? (
                    <p className="text-sm text-grey-300 py-4">
                        {folderNameSearch
                            ? t('folders.noFoldersMatchSearch')
                            : t('folders.noFoldersToMove')}
                    </p>
                ) : (
                    orderedFolders.map((folder) => {
                        const state = getFolderMembershipState(
                            folder.id,
                            targetMembership,
                            totalSources,
                            isDescribed,
                        );
                        const memberCount =
                            targetMembership[folder.id]?.size ?? 0;
                        const showPartial = !isSingle && state === 'partial';
                        const isActive = state !== 'none';

                        const FolderItemIcon = isActive
                            ? FolderOpenIcon
                            : FolderIcon;

                        return (
                            <ListItem
                                key={folder.id}
                                variant="short"
                                title={
                                    <span
                                        className={cn(
                                            'text-sm-medium',
                                            isActive
                                                ? 'text-white-100'
                                                : 'text-grey-200',
                                        )}
                                    >
                                        {folder.name}
                                    </span>
                                }
                                onClick={() => toggleFolder(folder.id)}
                                data-testid={`manage-folders-modal-item-${folder.id}`}
                                startContent={
                                    <FolderItemIcon
                                        className={cn(
                                            'size-3.5',
                                            isActive
                                                ? 'text-white-100'
                                                : 'text-grey-200',
                                        )}
                                        aria-hidden
                                    />
                                }
                                endContent={
                                    showPartial ? (
                                        <span className="text-xs text-grey-300">
                                            {t('folders.partialMembership', {
                                                count: memberCount,
                                                total: totalSources,
                                            })}
                                        </span>
                                    ) : state === 'all' ? (
                                        <CheckIcon
                                            className="size-4 text-white-100"
                                            weight="bold"
                                            aria-hidden
                                        />
                                    ) : null
                                }
                            />
                        );
                    })
                )}
                {isLoadingMore && (
                    <p className="text-sm text-grey-300 py-2 text-center">
                        {t('loadingMore')}
                    </p>
                )}
            </div>
        </Modal>
    );
}
